<template>
  <div
    class="GenericSideMenu modalContent modalHeight
    fixed top-0 z-modalsContent translate-x-[0%] flex"
    :class="[ position, modalSizeClass ]"
  >
    <!-- extension -->
    <div
      v-if="withExtension"
      class="flex flex-col border-border-base border-r h-full w-full max-w-[225px] overflow-y-scroll overflow-x-clip"
    >
      <slot name="extended" />
    </div>

    <div class="flex flex-col modalContentWidth h-full">
      <!-- head -->
      <div
        class="shrink-0 grow-0 basis-56 flex w-full items-center justify-between gap-12 px-16 border-b border-light select-none"
        :class="[ headerClass ]"
      >
        <button
          v-if="showBack"
          class="w-[30px] h-[30px] cursor-pointer"
          @click="$emit('back')"
        >
          <img
            src="/icons/arrow-left.svg"
            class="h-full w-full"
            alt="Back"
          >
        </button>
        <div v-if="title !== '' && titleDescription !== ''" class="flex py-12 flex-col justify-between gap-4">
          <div class="type-headline-lg leading-single flex items-start">
            {{ title }}
            <span v-if="titleMeta !== null" class="ml-8">({{ titleMeta }})</span>
          </div>
          <div class="!type-sm text-darker">
            {{ titleDescription }}
          </div>
        </div>
        <div v-else-if="title !== ''" class="type-headline-lg leading-single flex items-start">
          {{ title }}
          <span v-if="titleMeta !== null" class="ml-8">({{ titleMeta }})</span>
        </div>
        <div v-else class="h-[22px]">
          <img
            v-if="showLogo"
            :src="`/logos/${globalContent.getLogoUrl}-inv.svg`"
            alt="logo"
            class="h-[22px]"
          >
        </div>
        <button class="w-[24px] h-[24px] text-right cursor-pointer hover:bg-bg-subtle outline outline-4 outline-transparent hover:outline-bg-subtle rounded-6 transition-all" @click="closeMenu">
          <img
            src="/icons/close.svg"
            class="h-full w-full"
            alt="Close"
          >
        </button>
      </div>

      <!-- content -->
      <div
        class="p-16 desk:p-24 basis-full overflow-auto"
        :class="[ contentClass ]"
      >
        <slot />
      </div>

      <!-- bottom -->
      <div
        class="bg-lighter shrink-0 empty:hidden"
        :class="[ bottomClass ]"
        @click="closeMenu"
      >
        <slot name="bottom" />
      </div>
      <Teleport v-if="selfContained && !noBackground" to="body">
        <div
          class="modalBg modalHeight"
          @click="$emit('close')"
        />
      </Teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUiStore } from '~/store/ui';
import { useGlobalContentStore } from '~/store/globalContent';
const globalContent = useGlobalContentStore();

const emit = defineEmits(['back', 'close']);
const uiStore = useUiStore();

interface Props {
  title?: string,
  titleMeta?: number | null,
  titleDescription?: string,
  showBack?: boolean,
  showLogo?: boolean,
  position?: 'left' | 'right',
  headerClass?: string,
  contentClass?: string,
  bottomClass?: string,
  selfContained?: boolean, // comes with background, does not use ui store
  noBackground?: boolean, // for self contained
  small?: boolean;
  withExtension?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  titleMeta: null,
  titleDescription: '',
  showBack: false,
  showLogo: false,
  position: 'left',
  headerClass: '',
  contentClass: 'p-16',
  bottomClass: 'basis-[75px]',
  selfContained: false,
  noBackground: false,
  small: false,
  withExtension: false,
});

const modalSizeClass = computed(() => {
  if (props.withExtension) {
    return 'modalContentWidthWithExtension';
  }
  return props.small ? 'modalContentWidthSmall flex-col' : 'modalContentWidth flex-col';
});

const closeMenu = () => {
  if (!props.selfContained) {
    uiStore.closeAllModal();
  }
  emit('close');
};

</script>

<style scoped lang="postcss">
.left {
  @apply left-0;
}
.right {
  @apply right-0;
}
</style>
